'use client';

import { useMemo, useState } from 'react';
import { formatDiamond } from '@/utils/formatDiamond';
import { Box, Container, Typography, ButtonBase, Skeleton } from '@mui/material';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Icon } from '@/components/icons';
import { useTranslator } from '@/lang';
import { useDarkColors } from '@/hooks/useDarkColors';
import { usePortalStore } from '@/stores/portalStore';
import { useAuthStore } from '@/stores/authStore';
import { useLoginModalStore } from '@/stores/loginModalStore';
import {
  TEMP_FG_INVERSE,
  TEMP_FG_DIAMOND,
  TEMP_FG_ACCENT,
} from '@/constants/tempColors';
import {
  TEMP_TEXT_BOLD_XXL,
  TEMP_TEXT_BOLD_XL,
  TEMP_TEXT_BOLD_M,
  TEMP_TEXT_BOLD_XXS,
  TEMP_TEXT_SEMIBOLD_XL,
  TEMP_TEXT_SEMIBOLD_XS,
  TEMP_TEXT_MEDIUM_XS,
  TEMP_TEXT_MEDIUM_M,
} from '@/constants/tempTypo';
import {
  getAttendanceCycle,
  checkAttendance,
  type AttendanceCycleDay,
  type AttendanceCycleResult,
  type AttendanceCheckResult,
} from '@/api/attendanceApi';
import { getPackageList } from '@/api/feedApi';
import { getLocalizedText } from '@/utils/getLocalizedText';
import { ProPill, BasicPill, formatDday } from '@/components/MembershipPill';
import { getPassRemaining } from '@/lib/billing/pass';

const EASING: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

const PRO_GRADIENT = 'linear-gradient(100deg, #572dd6 0%, #a134e5 100%)';
const BASIC_GRADIENT = 'linear-gradient(100deg, #0284c7 0%, #38bdf8 100%)';
const BONUS_GRADIENT = 'linear-gradient(135deg, #B388FF 0%, #6A5BFF 100%)';
const FINAL_GRADIENT = 'linear-gradient(135deg, #FFD86B 0%, #FF7E3D 100%)';
const COMPLETED_GRADIENT = 'linear-gradient(135deg, #6A5BFF 0%, #4B00CC 100%)';
const HERO_BG_IMAGE = '/images/withdraw_feedback.png';

/** 비로그인 유저용 기본 사이클 데이터 — 28일 모두 미체크 상태 */
function buildEmptyCycle(): AttendanceCycleResult {
  const S_BONUS_DAYS = new Set<number>([7, 14, 21, 28]);
  const days: AttendanceCycleDay[] = [];
  for (let i = 1; i <= 28; i++) {
    days.push({
      dayIndex: i,
      checked: false,
      checkDate: null,
      rewardDiamond: 0,
      bonusDiamond: 0,
      bonusTicket: 0,
      isMuseProAtCheck: false,
      isBonusDay: i % 7 === 0,
      isTicketDay: i % 4 === 0,
      rIntimacyItemCount: 2,
      sIntimacyItemCount: S_BONUS_DAYS.has(i) ? 4 : 0,
    });
  }
  return {
    checkedToday: false,
    currentStreak: 0,
    cycleDay: 0,
    cycleNumber: 1,
    cycleLength: 28,
    isMuseProActive: false,
    museProExpiresAt: null,
    isMuseBasicActive: false,
    museBasicExpiresAt: null,
    nextRewardDiamond: 10, // 첫 출석은 1일차 → 10
    firstDayRewardDiamond: 10,
    normalRewardDiamond: 35,
    proRewardDiamond: 250,
    bonusDiamond: 15,
    bonusTicket: 1,
    finalBonusDiamond: 65,
    finalBonusTicket: 2,
    bonusStreakInterval: 7,
    ticketStreakInterval: 4,
    days,
  };
}

export default function AttendanceContent() {
  const c = useDarkColors();
  const { t } = useTranslator();
  const qc = useQueryClient();
  const openModal = usePortalStore((s) => s.openModal);
  const closeById = usePortalStore((s) => s.closeById);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const openLoginModal = useLoginModalStore((s) => s.open);

  const { data: fetched, isLoading, isError } = useQuery({
    queryKey: ['attendance-cycle'],
    queryFn: getAttendanceCycle,
    enabled: isLoggedIn,
    staleTime: 1000 * 30,
  });

  // 비로그인 시 빈 사이클 데이터로 화면을 그대로 보여줌 — 출석체크 누를 때만 로그인 모달
  const data = isLoggedIn ? fetched : buildEmptyCycle();

  const [submitting, setSubmitting] = useState(false);

  const checkInMutation = useMutation({
    mutationFn: checkAttendance,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['attendance-cycle'] });
      qc.invalidateQueries({ queryKey: ['attendance-status'] });
      qc.invalidateQueries({ queryKey: ['balance'] });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      showRewardModal(res);
    },
    onError: () => {
      // 이미 출석 처리됐을 가능성 — 사이클을 다시 불러 강제 동기화
      qc.invalidateQueries({ queryKey: ['attendance-cycle'] });
      qc.invalidateQueries({ queryKey: ['attendance-status'] });
    },
    onSettled: () => setSubmitting(false),
  });

  const handleCheckIn = () => {
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }
    if (submitting || !data || data.checkedToday) return;
    setSubmitting(true);
    checkInMutation.mutate();
  };

  const showRewardModal = (result: AttendanceCheckResult) => {
    let id = '';
    id = openModal({
      content: (
        <RewardModal
          result={result}
          onConfirm={() => closeById(id)}
        />
      ),
    });
  };

  // 4칸 x 7줄 레이아웃 — 한 줄에 4일씩, 총 7줄 (28일)
  const rows: AttendanceCycleDay[][] = useMemo(() => {
    if (!data) return [];
    const out: AttendanceCycleDay[][] = [];
    for (let i = 0; i < 7; i++) {
      out.push(data.days.slice(i * 4, i * 4 + 4));
    }
    return out;
  }, [data]);

  if (isLoggedIn && isLoading) {
    return <AttendanceSkeleton />;
  }

  if (isLoggedIn && (isError || !data)) {
    return (
      <Container maxWidth="sm" disableGutters>
        <Box sx={{ px: '20px', py: '40px', textAlign: 'center' }}>
          <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fgLow }}>
            {t('attendance.load_error')}
          </Typography>
        </Box>
      </Container>
    );
  }

  // data 는 위 가드에서 보장됨 (비로그인은 buildEmptyCycle, 로그인은 fetched)
  if (!data) return null;

  return (
    <Container
      maxWidth="sm"
      disableGutters
      sx={{ pb: '120px', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        sx={{
          px: { xs: '20px', sm: '24px' },
          pt: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '16px', sm: '20px' },
        }}
      >
        <StreakHero cycle={data} isLoggedIn={isLoggedIn} />
        <CycleGrid rows={rows} cycle={data} />
        <RewardSummary cycle={data} />
      </Box>

      <CheckInBar
        cycle={data}
        submitting={submitting}
        isLoggedIn={isLoggedIn}
        onCheckIn={handleCheckIn}
      />
    </Container>
  );
}


// ============================================================
// 상단 Hero: 연속 출석 현황
// ============================================================

function StreakHero({
  cycle,
  isLoggedIn,
}: {
  cycle: AttendanceCycleResult;
  isLoggedIn: boolean;
}) {
  const { t, f } = useTranslator();
  const cycleDay = cycle.cycleDay;
  const cycleLength = cycle.cycleLength;
  const progressPct = cycleLength === 0 ? 0 : Math.min(100, (cycleDay / cycleLength) * 100);

  // BASIC 잔여 카운트는 ['package-list'] 캐시에서 — 채팅방/충전 페이지와 공유.
  const { data: pkgData } = useQuery({
    queryKey: ['package-list'],
    queryFn: getPackageList,
    enabled: isLoggedIn && (cycle.isMuseProActive || cycle.isMuseBasicActive),
    staleTime: 1000 * 30,
  });
  const proDday = formatDday(cycle.museProExpiresAt);
  const basicDday = formatDday(cycle.museBasicExpiresAt);
  // 새 서버는 entitlements.pass(주간) · 옛 서버는 museBasicDailyFree(일간)
  const passRemaining = getPassRemaining(pkgData);
  const basicChatRemaining = passRemaining?.chatRemaining ?? null;
  const basicTtsRemaining = passRemaining?.ttsRemaining ?? null;

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '20px',
        px: { xs: '24px', sm: '28px' },
        py: { xs: '24px', sm: '28px' },
        overflow: 'hidden',
        bgcolor: '#1a0f3b',
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
      }}
    >
      {/* 배경 이미지 — withdraw_feedback.png */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${HERO_BG_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          pointerEvents: 'none',
        }}
      />
      {/* 가독성 확보용 딤 — 이미지가 살짝 비치도록 알파 낮춤 */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(135deg, rgba(45,26,94,0.55) 0%, rgba(26,15,59,0.72) 100%)',
          pointerEvents: 'none',
        }}
      />
      {/* 텍스트 영역 가독성 보강 — 좌측에서 약한 그림자 */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(90deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 60%)',
          pointerEvents: 'none',
        }}
      />
      {/* 빛나는 배경 장식 */}
      <Box
        sx={{
          position: 'absolute',
          top: -40,
          right: -40,
          width: 180,
          height: 180,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(255,216,107,0.18) 0%, rgba(255,255,255,0) 70%)',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: { xs: '14px', sm: '18px' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Box
            sx={{
              px: { xs: '10px', sm: '12px' },
              py: { xs: '4px', sm: '5px' },
              borderRadius: '999px',
              bgcolor: 'rgba(255,255,255,0.18)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <Typography
              sx={{
                ...TEMP_TEXT_BOLD_XXS,
                color: TEMP_FG_INVERSE,
                letterSpacing: '0.5px',
                fontSize: { xs: 11, sm: 12 },
              }}
            >
              {f(t('attendance.cycle_number'), [cycle.cycleNumber])}
            </Typography>
          </Box>
          {cycle.isMuseProActive && <ProPill dday={proDday} />}
          {cycle.isMuseBasicActive && (
            <BasicPill
              dday={basicDday}
              chatRemaining={basicChatRemaining}
              ttsRemaining={basicTtsRemaining}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '10px', sm: '14px' } }}>
          {/* 불 Lottie — 항상 노출 (1일 출석부터든 미출석이든 상시 표시) */}
          <Box
            sx={{
              width: { xs: 56, sm: 72 },
              height: { xs: 56, sm: 72 },
              flexShrink: 0,
              mt: '-4px',
              opacity: cycle.currentStreak > 0 ? 1 : 0.85,
            }}
          >
            <DotLottieReact
              src="/lottie/fire.lottie"
              loop
              autoplay
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                ...TEMP_TEXT_BOLD_XXL,
                color: TEMP_FG_INVERSE,
                lineHeight: 1.15,
                fontSize: { xs: 22, sm: 28 },
              }}
            >
              {cycle.currentStreak > 0 ? (
                <>
                  <Box component="span" sx={{ fontWeight: 900 }}>{f(t('attendance.hero_streak_days'), [cycle.currentStreak])}</Box> {t('attendance.hero_streak_suffix')}
                </>
              ) : !isLoggedIn ? (
                <>{t('attendance.hero_login_cta')}</>
              ) : (
                <>{t('attendance.hero_today_cta')}</>
              )}
            </Typography>
            <Typography
              sx={{
                mt: '6px',
                ...TEMP_TEXT_MEDIUM_M,
                color: 'rgba(255,255,255,0.85)',
                fontSize: { xs: 13, sm: 15 },
              }}
            >
              {!isLoggedIn
                ? t('attendance.hero_sub_guest')
                : cycle.checkedToday
                ? t('attendance.hero_sub_done')
                : cycle.currentStreak > 0
                ? f(t('attendance.hero_sub_streak'), [cycle.currentStreak + 1])
                : t('attendance.hero_sub_start')}
            </Typography>
            <Typography
              sx={{
                mt: '4px',
                fontSize: { xs: '12px', sm: '13px' },
                fontWeight: 500,
                color: 'rgba(255,255,255,0.55)',
                letterSpacing: '0.1px',
              }}
            >
              {t('attendance.hero_reset_note')}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Box
            sx={{
              height: { xs: 8, sm: 10 },
              borderRadius: '999px',
              bgcolor: 'rgba(255,255,255,0.18)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${progressPct}%`,
                background: 'linear-gradient(90deg, #FFD86B 0%, #FFFFFF 100%)',
                borderRadius: '999px',
                transition: 'width 0.4s ease',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '6px' }}>
            <Typography
              sx={{
                ...TEMP_TEXT_BOLD_XXS,
                color: 'rgba(255,255,255,0.75)',
                fontSize: { xs: 11, sm: 12 },
              }}
            >
              {f(t('attendance.progress_days'), [cycleDay, cycleLength])}
            </Typography>
            <Typography
              sx={{
                ...TEMP_TEXT_BOLD_XXS,
                color: 'rgba(255,255,255,0.75)',
                fontSize: { xs: 11, sm: 12 },
              }}
            >
              {f(t('attendance.days_left'), [cycleLength - cycleDay])}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ============================================================
// 28일 그리드
// ============================================================

function CycleGrid({
  rows,
  cycle,
}: {
  rows: AttendanceCycleDay[][];
  cycle: AttendanceCycleResult;
}) {
  const c = useDarkColors();
  const { t } = useTranslator();
  const nextDayIndex = cycle.checkedToday ? -1 : cycle.cycleDay + 1; // 오늘 받을 칸

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <Typography
        sx={{
          ...TEMP_TEXT_SEMIBOLD_XS,
          color: c.fgLow,
          letterSpacing: '0.5px',
          fontSize: { xs: 12, sm: 14 },
        }}
      >
        {t('attendance.mission_title')}
      </Typography>
      <Box
        sx={{
          bgcolor: c.bgCard,
          border: `1px solid ${c.border}`,
          borderRadius: '20px',
          px: { xs: '12px', sm: '16px' },
          py: { xs: '14px', sm: '18px' },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '10px', sm: '12px' },
        }}
      >
        {rows.map((row, ri) => (
          <Box
            key={ri}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: { xs: '10px', sm: '12px' },
            }}
          >
            {row.map((day) => (
              <DayCell
                key={day.dayIndex}
                day={day}
                cycle={cycle}
                isNext={day.dayIndex === nextDayIndex}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function DayCell({
  day,
  cycle,
  isNext,
}: {
  day: AttendanceCycleDay;
  cycle: AttendanceCycleResult;
  isNext: boolean;
}) {
  const c = useDarkColors();
  const { t, f } = useTranslator();
  const isBonus = day.isBonusDay;
  // 뽑기권 날 (4일 주기) — 구버전 API 응답에 isTicketDay 가 없으면 dayIndex 로 계산
  const isTicket = day.isTicketDay ?? day.dayIndex % (cycle.ticketStreakInterval ?? 4) === 0;
  const isFinal = day.dayIndex === cycle.cycleLength; // 28일 완주 칸
  const isPro = day.isMuseProAtCheck;
  const checked = day.checked;
  // 미체크 셀의 미리보기 보너스 (28일은 finalBonus 사용). 다이아는 7일 주기, 뽑기권은 4일 주기.
  const previewBonusDiamond = isFinal
    ? cycle.finalBonusDiamond
    : isBonus
    ? cycle.bonusDiamond
    : 0;
  const previewBonusTicket = isFinal
    ? cycle.finalBonusTicket
    : isTicket
    ? cycle.bonusTicket
    : 0;
  const previewPro = !checked && cycle.isMuseProActive;
  // 미체크 셀 기본 보상 — 1일차는 무조건 10, 그 외는 PRO/일반 분기
  const previewBase =
    day.dayIndex === 1
      ? cycle.firstDayRewardDiamond
      : cycle.isMuseProActive
      ? cycle.proRewardDiamond
      : cycle.normalRewardDiamond;
  const previewReward = previewBase + previewBonusDiamond;
  const actualReward = day.rewardDiamond + day.bonusDiamond;
  const ticketCount = checked ? day.bonusTicket : previewBonusTicket;

  const background = checked
    ? isFinal
      ? FINAL_GRADIENT
      : isBonus
      ? BONUS_GRADIENT
      : COMPLETED_GRADIENT
    : isFinal
    ? c.isDark
      ? 'linear-gradient(135deg, rgba(255,216,107,0.15) 0%, rgba(255,126,61,0.10) 100%)'
      : 'linear-gradient(135deg, rgba(255,216,107,0.18) 0%, rgba(255,126,61,0.10) 100%)'
    : isBonus
    ? c.isDark
      ? 'linear-gradient(135deg, rgba(106,91,255,0.18) 0%, rgba(75,0,204,0.12) 100%)'
      : 'linear-gradient(135deg, rgba(106,91,255,0.10) 0%, rgba(75,0,204,0.06) 100%)'
    : c.bgInset;

  const border = isNext
    ? `2px solid ${TEMP_FG_ACCENT}`
    : checked
    ? 'none'
    : isFinal
    ? `1px solid ${c.isDark ? 'rgba(255,158,61,0.45)' : 'rgba(255,126,61,0.35)'}`
    : isBonus
    ? `1px solid ${c.isDark ? 'rgba(106,91,255,0.4)' : 'rgba(75,0,204,0.25)'}`
    : `1px solid ${c.border}`;

  const textColor = checked ? TEMP_FG_INVERSE : c.fg;
  const dimColor = checked ? 'rgba(255,255,255,0.78)' : c.fgLow;

  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: { xs: '1 / 1.3', sm: '1 / 1.05' },
        borderRadius: { xs: '14px', sm: '16px' },
        background,
        border,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: '3px', sm: '6px' },
        px: { xs: '4px', sm: '8px' },
        py: { xs: '6px', sm: '10px' },
        overflow: 'hidden',
        boxShadow: isNext
          ? '0 0 0 3px rgba(247,85,85,0.22)'
          : !checked && isFinal
          ? c.isDark
            ? '0 4px 14px rgba(255,126,61,0.22)'
            : '0 4px 14px rgba(255,126,61,0.18)'
          : !checked && isBonus
          ? c.isDark
            ? '0 4px 14px rgba(106,91,255,0.18)'
            : '0 4px 14px rgba(75,0,204,0.12)'
          : 'none',
      }}
    >
      {/* PRO 배지 — 별 + PRO 텍스트, 모바일에선 컴팩트 사이즈 */}
      {(isPro || previewPro) && (
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 3, sm: 6 },
            right: { xs: 3, sm: 6 },
            display: 'inline-flex',
            alignItems: 'center',
            gap: { xs: '1px', sm: '2px' },
            background: PRO_GRADIENT,
            borderRadius: '9999px',
            px: { xs: '4px', sm: '7px' },
            height: { xs: 13, sm: 18 },
          }}
        >
          <Icon name="icStar" size={7} color="#ffd04d" />
          <Typography
            sx={{
              fontSize: { xs: '7.5px', sm: '10px' },
              fontWeight: 800,
              color: '#fff',
              lineHeight: { xs: '13px', sm: '18px' },
              letterSpacing: '0.1px',
            }}
          >
            PRO
          </Typography>
        </Box>
      )}

      {/* 28일 완주 왕관 — PRO 배지 없을 때만 */}
      {isFinal && !(isPro || previewPro) && (
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 2, sm: 6 },
            right: { xs: 4, sm: 8 },
            fontSize: { xs: '11px', sm: '15px' },
            lineHeight: 1,
          }}
        >
          👑
        </Box>
      )}

      {/* 일자 — PRO/왕관 배지가 있는 보너스 셀은 위쪽 여백 충분히 두기 */}
      <Typography
        sx={{
          fontSize: {
            xs: isBonus ? '10px' : '11px',
            sm: '13px',
          },
          fontWeight: 700,
          color: dimColor,
          letterSpacing: '0.3px',
          lineHeight: 1,
          mt: {
            xs: (isPro || previewPro) ? '10px' : isBonus ? '6px' : '2px',
            sm: (isPro || previewPro) ? '6px' : '2px',
          },
        }}
      >
        {f(t('attendance.day_n'), [day.dayIndex])}
      </Typography>

      {/* 다이아 보상 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '4px', sm: '5px' } }}>
        <Box
          sx={{
            width: { xs: 16, sm: 20 },
            height: { xs: 16, sm: 20 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon
            name="icDiamondFilled"
            size={20}
            color={checked ? TEMP_FG_INVERSE : TEMP_FG_DIAMOND}
          />
        </Box>
        <Typography
          sx={{
            fontSize: { xs: '14px', sm: '17px' },
            fontWeight: 800,
            color: textColor,
            lineHeight: 1,
          }}
        >
          {checked ? actualReward : previewReward}
        </Typography>
      </Box>

      {/* 뽑기권 (4일 주기 + 28일 완주) */}
      {ticketCount > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '3px', sm: '4px' } }}>
          <Box
            component="img"
            src="/svgs/ticket.svg"
            alt=""
            sx={{
              width: { xs: 14, sm: 16 },
              height: { xs: 14, sm: 16 },
              objectFit: 'contain',
              filter: checked ? 'brightness(0) invert(1)' : 'none',
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: '11px', sm: '13px' },
              fontWeight: 700,
              color: textColor,
              lineHeight: 1,
            }}
          >
            ×{ticketCount}
          </Typography>
        </Box>
      )}

      {/* 친밀도 +2 (R등급) — 매일, 7/14/21/28일차 제외 */}
      {day.rIntimacyItemCount > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '4px', sm: '6px' } }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '1px', lineHeight: 0 }}>
            <Icon
              name="icHeartFilled"
              size={12}
              color={checked ? TEMP_FG_INVERSE : TEMP_FG_ACCENT}
            />
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 12,
                fontSize: { xs: '10px', sm: '12px' },
                fontWeight: 700,
                color: checked ? TEMP_FG_INVERSE : TEMP_FG_ACCENT,
                lineHeight: 1,
              }}
            >
              2
            </Box>
          </Box>
          {day.rIntimacyItemCount > 1 && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 12,
                fontSize: { xs: '10px', sm: '12px' },
                fontWeight: 700,
                color: textColor,
                lineHeight: 1,
              }}
            >
              ×{day.rIntimacyItemCount}
            </Box>
          )}
        </Box>
      )}

      {/* 친밀도 +4 (S등급) — 7/14/21/28일차 */}
      {day.sIntimacyItemCount > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '4px', sm: '6px' } }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '1px', lineHeight: 0 }}>
            <Icon
              name="icHeartFilled"
              size={13}
              color={checked ? TEMP_FG_INVERSE : '#FE375B'}
            />
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 13,
                fontSize: { xs: '10px', sm: '12px' },
                fontWeight: 800,
                color: checked ? TEMP_FG_INVERSE : '#FE375B',
                lineHeight: 1,
              }}
            >
              4
            </Box>
          </Box>
          {day.sIntimacyItemCount > 1 && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 13,
                fontSize: { xs: '10px', sm: '12px' },
                fontWeight: 800,
                color: textColor,
                lineHeight: 1,
              }}
            >
              ×{day.sIntimacyItemCount}
            </Box>
          )}
        </Box>
      )}

      {/* 완료 체크 — 좌상단 작은 체크 동그라미 (모바일 컨텐츠 영역 침범 방지) */}
      {checked && (
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 4, sm: 6 },
            left: { xs: 4, sm: 6 },
            width: { xs: 12, sm: 14 },
            height: { xs: 12, sm: 14 },
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Icon name="icCheck" size={9} color="#4B00CC" />
        </Box>
      )}

      {/* '오늘' 점멸 인디케이터 */}
      {isNext && (
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            top: 6,
            left: 6,
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: TEMP_FG_ACCENT,
            boxShadow: `0 0 6px ${TEMP_FG_ACCENT}`,
          }}
        />
      )}
    </Box>
  );
}

// ============================================================
// 오늘의 보상 요약 카드
// ============================================================

function RewardSummary({ cycle }: { cycle: AttendanceCycleResult }) {
  const c = useDarkColors();
  const { t, f } = useTranslator();
  const nextDay = cycle.checkedToday ? cycle.cycleDay : cycle.cycleDay + 1;
  const isFinalDay = nextDay === cycle.cycleLength;
  const isBonusDay = nextDay > 0 && nextDay % cycle.bonusStreakInterval === 0 && nextDay <= cycle.cycleLength;
  const isTicketDay =
    nextDay > 0 && nextDay % (cycle.ticketStreakInterval ?? 4) === 0 && nextDay <= cycle.cycleLength;
  const bonusDiamond = isFinalDay
    ? cycle.finalBonusDiamond
    : isBonusDay
    ? cycle.bonusDiamond
    : 0;
  const bonusTicket = isFinalDay
    ? cycle.finalBonusTicket
    : isTicketDay
    ? cycle.bonusTicket
    : 0;
  const hasBonus = isBonusDay || bonusTicket > 0;
  const todayDiamond = cycle.nextRewardDiamond + bonusDiamond;
  const todayDay = cycle.days.find((d) => d.dayIndex === nextDay) ?? null;
  const todayRItemCount = todayDay?.rIntimacyItemCount ?? 0;
  const todaySItemCount = todayDay?.sIntimacyItemCount ?? 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <Typography
        sx={{
          ...TEMP_TEXT_SEMIBOLD_XS,
          color: c.fgLow,
          letterSpacing: '0.5px',
          fontSize: { xs: 12, sm: 14 },
        }}
      >
        {t('attendance.today_reward_title')}
      </Typography>
      <Box
        sx={{
          bgcolor: c.bgCard,
          border: `1px solid ${c.border}`,
          borderRadius: '16px',
          px: { xs: '18px', sm: '22px' },
          py: { xs: '16px', sm: '20px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
          <Typography
            sx={{
              ...TEMP_TEXT_SEMIBOLD_XS,
              color: c.fgLow,
              fontSize: { xs: 12, sm: 13 },
            }}
          >
            {cycle.checkedToday ? t('attendance.reward_received_today') : f(t('attendance.day_n_reward'), [nextDay])}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icon name="icDiamondFilled" size={22} color={TEMP_FG_DIAMOND} />
              <Typography
                sx={{
                  ...TEMP_TEXT_BOLD_XL,
                  color: c.fg,
                  fontSize: { xs: 19, sm: 22 },
                }}
              >
                {todayDiamond}
              </Typography>
            </Box>
            {bonusTicket > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Box
                  component="img"
                  src="/svgs/ticket.svg"
                  alt=""
                  sx={{ width: 22, height: 22, objectFit: 'contain' }}
                />
                <Typography
                  sx={{
                    ...TEMP_TEXT_BOLD_XL,
                    color: c.fg,
                    fontSize: { xs: 19, sm: 22 },
                  }}
                >
                  x{bonusTicket}
                </Typography>
              </Box>
            )}
            {todayRItemCount > 0 && (
              // 칸 그리드 R등급과 동일 포맷: [♥]2 ×N (×는 count > 1 일 때만)
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '1px', lineHeight: 0 }}>
                  <Icon name="icHeartFilled" size={20} color={TEMP_FG_ACCENT} />
                  <Box
                    component="span"
                    sx={{
                      ...TEMP_TEXT_BOLD_XL,
                      color: TEMP_FG_ACCENT,
                      fontSize: { xs: 17, sm: 20 },
                      lineHeight: 1,
                    }}
                  >
                    2
                  </Box>
                </Box>
                {todayRItemCount > 1 && (
                  <Box
                    component="span"
                    sx={{
                      ...TEMP_TEXT_BOLD_XL,
                      color: c.fg,
                      fontSize: { xs: 17, sm: 20 },
                      lineHeight: 1,
                    }}
                  >
                    ×{todayRItemCount}
                  </Box>
                )}
              </Box>
            )}
            {todaySItemCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '1px', lineHeight: 0 }}>
                  <Icon name="icHeartFilled" size={22} color="#FE375B" />
                  <Box
                    component="span"
                    sx={{
                      ...TEMP_TEXT_BOLD_XL,
                      color: '#FE375B',
                      fontSize: { xs: 19, sm: 22 },
                      lineHeight: 1,
                    }}
                  >
                    4
                  </Box>
                </Box>
                {todaySItemCount > 1 && (
                  <Box
                    component="span"
                    sx={{
                      ...TEMP_TEXT_BOLD_XL,
                      color: c.fg,
                      fontSize: { xs: 19, sm: 22 },
                      lineHeight: 1,
                    }}
                  >
                    ×{todaySItemCount}
                  </Box>
                )}
              </Box>
            )}
          </Box>
          {cycle.isMuseProActive && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '2px', flexWrap: 'wrap' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  background: PRO_GRADIENT,
                  borderRadius: '9999px',
                  px: { xs: '7px', sm: '9px' },
                  height: { xs: 16, sm: 18 },
                }}
              >
                <Icon name="icStar" size={9} color="#ffd04d" />
                <Typography
                  sx={{
                    fontSize: { xs: 9, sm: 10 },
                    fontWeight: 700,
                    color: '#fff',
                    lineHeight: { xs: '16px', sm: '18px' },
                    letterSpacing: '0.2px',
                  }}
                >
                  {t('attendance.pro_applied')}
                </Typography>
              </Box>
              <Typography
                sx={{
                  ...TEMP_TEXT_MEDIUM_XS,
                  color: c.fgLow,
                  fontSize: { xs: 11, sm: 12 },
                }}
              >
                {f(t('attendance.pro_boost_note'), [cycle.proRewardDiamond])}
              </Typography>
            </Box>
          )}
        </Box>
        {hasBonus && (
          <Box
            sx={{
              px: { xs: '10px', sm: '14px' },
              py: { xs: '6px', sm: '8px' },
              borderRadius: '10px',
              background: isFinalDay ? FINAL_GRADIENT : BONUS_GRADIENT,
              textAlign: 'center',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                ...TEMP_TEXT_BOLD_XXS,
                color: TEMP_FG_INVERSE,
                letterSpacing: '0.5px',
                fontSize: { xs: 11, sm: 12 },
              }}
            >
              {isFinalDay ? t('attendance.final_badge') : t('attendance.bonus_badge')}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '9px', sm: '11px' },
                fontWeight: 700,
                color: 'rgba(255,255,255,0.92)',
                mt: '2px',
              }}
            >
              {[
                bonusDiamond > 0 ? f(t('attendance.bonus_diamond'), [bonusDiamond]) : null,
                bonusTicket > 0 ? f(t('attendance.bonus_ticket'), [bonusTicket]) : null,
              ]
                .filter(Boolean)
                .join(' ')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ============================================================
// 하단 고정 CTA 바
// ============================================================

function CheckInBar({
  cycle,
  submitting,
  isLoggedIn,
  onCheckIn,
}: {
  cycle: AttendanceCycleResult;
  submitting: boolean;
  isLoggedIn: boolean;
  onCheckIn: () => void;
}) {
  const c = useDarkColors();
  const { t } = useTranslator();
  const done = isLoggedIn && cycle.checkedToday;
  // 비로그인일 땐 항상 누를 수 있어야 — 누르면 로그인 모달이 뜸
  const disabled = done || submitting;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: 600,
        px: { xs: '20px', sm: '32px' },
        pt: { xs: '12px', sm: '16px' },
        pb: 'calc(20px + env(safe-area-inset-bottom))',
        bgcolor: c.isDark ? 'rgba(18,18,18,0.92)' : 'rgba(255,255,255,0.94)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: `0.5px solid ${c.border}`,
        zIndex: 10,
      }}
    >
      <ButtonBase
        onClick={onCheckIn}
        disabled={disabled}
        sx={{
          width: '100%',
          py: { xs: '18px', sm: '22px' },
          borderRadius: '16px',
          background: done
            ? c.bgInset
            : 'linear-gradient(135deg, #6A5BFF 0%, #4B00CC 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          cursor: done ? 'default' : 'pointer',
          opacity: submitting ? 0.7 : 1,
          transition: 'opacity 0.2s ease',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {done ? (
          <>
            <Icon name="icCheck" size={22} color={c.fgLow} />
            <Typography
              sx={{
                ...TEMP_TEXT_SEMIBOLD_XL,
                color: c.fgLow,
                fontSize: { xs: 17, sm: 19 },
              }}
            >
              {t('attendance.cta_done')}
            </Typography>
          </>
        ) : !isLoggedIn ? (
          <Typography
            sx={{
              ...TEMP_TEXT_SEMIBOLD_XL,
              color: TEMP_FG_INVERSE,
              fontSize: { xs: 17, sm: 19 },
            }}
          >
            {t('attendance.cta_login')}
          </Typography>
        ) : (
          <>
            <Icon name="icDiamondFilled" size={22} color={TEMP_FG_INVERSE} />
            <Typography
              sx={{
                ...TEMP_TEXT_SEMIBOLD_XL,
                color: TEMP_FG_INVERSE,
                fontSize: { xs: 17, sm: 19 },
              }}
            >
              {submitting ? t('attendance.cta_submitting') : t('attendance.cta_checkin')}
            </Typography>
          </>
        )}
      </ButtonBase>
    </Box>
  );
}

// ============================================================
// 출석 보상 받은 후 모달
// ============================================================

function RewardModal({
  result,
  onConfirm,
}: {
  result: AttendanceCheckResult;
  onConfirm: () => void;
}) {
  const c = useDarkColors();
  const { t, f, language } = useTranslator();
  const totalDiamond = result.rewardAmount + result.bonusDiamond;
  const hasTicket = result.bonusTicket > 0;
  const isBonusDay = result.bonusDiamond > 0;
  const hasBonus = isBonusDay || hasTicket;
  // 완주 판정은 streak 기준 — PRO는 뽑기권 2배라 평일 보너스도 2장이 되므로 수량(>=2) 판정은 오작동한다 (백엔드 CYCLE_LENGTH=28)
  const isFinalDay = result.streakCount > 0 && result.streakCount % 28 === 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: '24px',
        pt: '32px',
        pb: '24px',
        gap: '20px',
      }}
    >
      <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '40px', lineHeight: 1 }}>
          {isFinalDay ? '👑' : hasBonus ? '🎁' : '✨'}
        </Typography>
        <Typography sx={{ ...TEMP_TEXT_BOLD_XXL, color: c.fg, mt: '4px' }}>
          {isFinalDay
            ? t('attendance.modal_final_title')
            : hasBonus
            ? f(t('attendance.modal_bonus_title'), [result.streakCount])
            : f(t('attendance.modal_day_title'), [result.streakCount])}
        </Typography>
        <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fgLow }}>
          {result.isMuseProActive
            ? t('attendance.modal_pro_sub')
            : t('attendance.modal_tomorrow_sub')}
        </Typography>
        {result.isMuseProActive && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              background: PRO_GRADIENT,
              borderRadius: '9999px',
              px: '8px',
              height: 20,
              mt: '4px',
            }}
          >
            <Icon name="icStar" size={10} color="#ffd04d" />
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: '#fff',
                lineHeight: '20px',
              }}
            >
              {t('attendance.modal_pro_badge')}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          bgcolor: c.bgInset,
          borderRadius: '16px',
          width: '100%',
          py: '20px',
          px: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icon name="icDiamondFilled" size={28} color={TEMP_FG_DIAMOND} />
            <Box>
              <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: c.fg }}>{t('attendance.diamond')}</Typography>
              {isBonusDay && (
                <Typography sx={{ ...TEMP_TEXT_MEDIUM_XS, color: c.fgLow }}>
                  {f(t('attendance.modal_base_bonus'), [formatDiamond(result.rewardAmount), formatDiamond(result.bonusDiamond)])}
                </Typography>
              )}
            </Box>
          </Box>
          <Typography sx={{ ...TEMP_TEXT_BOLD_XXL, color: c.fg }}>
            +{totalDiamond}
          </Typography>
        </Box>

        {hasTicket && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Box
                component="img"
                src="/svgs/ticket.svg"
                alt=""
                sx={{ width: 28, height: 28, objectFit: 'contain' }}
              />
              <Box>
                <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: c.fg }}>{t('attendance.ticket')}</Typography>
                <Typography sx={{ ...TEMP_TEXT_MEDIUM_XS, color: c.fgLow }}>
                  {t('attendance.streak_bonus_label')}
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ ...TEMP_TEXT_BOLD_XXL, color: c.fg }}>
              x{result.bonusTicket}
            </Typography>
          </Box>
        )}

        {(result.grantedItems ?? []).map((item) => {
          const itemName =
            (item.name && typeof item.name === 'object'
              ? getLocalizedText(item.name, language)
              : null) || item.itemKey;
          const isS = item.grade === 'S';
          return (
            <Box
              key={item.itemKey}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Icon
                  name="icHeartFilled"
                  size={28}
                  color={isS ? '#FE375B' : TEMP_FG_ACCENT}
                />
                <Box>
                  <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: c.fg }}>
                    {f(t('attendance.intimacy_item'), [item.intimacyReward])}
                  </Typography>
                  <Typography sx={{ ...TEMP_TEXT_MEDIUM_XS, color: c.fgLow }}>
                    {itemName}
                  </Typography>
                </Box>
              </Box>
              <Typography sx={{ ...TEMP_TEXT_BOLD_XXL, color: c.fg }}>
                x{item.quantity}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <ButtonBase
        onClick={onConfirm}
        sx={{
          width: '100%',
          py: '16px',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #6A5BFF 0%, #4B00CC 100%)',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG_INVERSE }}>
          {t('attendance.modal_confirm')}
        </Typography>
      </ButtonBase>
    </Box>
  );
}

// ============================================================
// 스켈레톤
// ============================================================

function AttendanceSkeleton() {
  const c = useDarkColors();
  return (
    <Container maxWidth="sm" disableGutters>
      <Box sx={{ px: '20px', pt: '8px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Skeleton variant="rounded" sx={{ borderRadius: '20px', height: 160, bgcolor: c.skeleton }} />
        <Skeleton variant="rounded" sx={{ borderRadius: '20px', height: 280, bgcolor: c.skeleton }} />
        <Skeleton variant="rounded" sx={{ borderRadius: '16px', height: 80, bgcolor: c.skeleton }} />
      </Box>
    </Container>
  );
}
